<template>
  <v-container
    id="extended-tables-view"
    fluid
    tag="section"
  >
    <material-card
      icon="far fa-image"
      icon-small
      color="primary"
      :title="$t('pageTitles.Homepage/BannersList')"
    >
      <template #subtitle>
        <div class="text-right text-h3">
          <app-btn
            color="success"
            class="px-2 ml-1"
            elevation="0"
            min-width="0"
            small
            to="/homepage/banners/new"
          >
            <v-icon
              small
              v-text="'fas fa-plus'"
            />
          </app-btn>
        </div>
      </template>

      <v-simple-table class="mt-4">
        <thead>
          <tr>
            <th>{{ $t('forms.banner_desktop') }}</th>
            <th>{{ $t('forms.label') }}</th>
            <th>{{ $t('forms.starts_at') }}</th>
            <th>{{ $t('forms.ends_at') }}</th>
            <th>{{ $t('forms.status') }}</th>
            <th class="text-right">
              {{ $t('forms.actions') }}
            </th>
          </tr>
        </thead>
        <tbody v-if="!banners || !banners.length">
          <tr>
            <td colspan="6">
              <i
                v-if="processing"
                class="fas fa-spin fa-spinner"
              ></i>
              <template v-else>
                {{ $t('forms.nodata') }}
              </template>
            </td>
          </tr>
        </tbody>
        <tbody v-else>
          <tr
            v-for="banner in banners"
            :key="`banner-list-${banner.id}`"
          >
            <td>
              <img
                v-if="thumbnail(banner)"
                :src="thumbnail(banner)"
                :alt="banner.label"
                class="banner-thumbnail"
              />
            </td>
            <td>{{ banner.label }}</td>
            <td>{{ formatDate(banner.starts_at) }}</td>
            <td>{{ formatDate(banner.ends_at) }}</td>
            <td>
              <v-chip
                small
                :color="status(banner).color"
                text-color="white"
              >
                {{ $t(`banner_status.${status(banner).key}`) }}
              </v-chip>
            </td>
            <td class="text-right table-actions">
              <v-btn
                color="success"
                class="px-2 ml-2"
                elevation="0"
                min-width="0"
                small
                :to="`/homepage/banners/${banner.id}`"
              >
                <v-icon
                  small
                  v-text="'far fa-edit'"
                />
              </v-btn>
              <app-btn
                color="error"
                class="px-2 ml-2"
                elevation="0"
                min-width="0"
                small
                @click="initiateBannerDelete(banner)"
              >
                <v-icon
                  small
                  v-text="'fas fa-trash'"
                />
              </app-btn>
            </td>
          </tr>
        </tbody>
      </v-simple-table>
    </material-card>
    <v-dialog
      v-model="showDeletionDialog"
      width="300"
    >
      <v-card>
        <v-card-text class="pt-4">
          {{ $t('delete_confirm') }}
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-btn
            color="gray"
            text
            @click="closeDeletionDialog"
          >
            {{ $t('cancel') }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="red"
            text
            @click="deleteBanner"
          >
            {{ $t('delete') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex';
import serverError from '../../mixins/serverError';
export default {
  name: 'BannersList',

  mixins: [serverError],

  data: () => ({
    banners: [],
    showDeletionDialog: false,
    bannerToDelete: null,
  }),

  computed: {
    ...mapGetters({
      allBanners: 'banner/getBanners',
      processing: 'global/getProcessing',
    }),
    /* Le bandeau réellement diffusé, calculé comme côté API : actif, et le plus récemment démarré. */
    currentBannerId() {
      const active = this.banners.filter(banner => this.isStarted(banner) && !this.isEnded(banner));
      return active.length ? active[0].id : null;
    },
  },

  created() {
    this.$store.commit('banner/setBanner', null);
    this.refreshData();
  },

  methods: {
    refreshData() {
      this.banners = [];
      this.$store.dispatch('banner/fetchBanners')
        .then(() => {
          this.banners = [...this.allBanners];
        });
    },
    thumbnail(banner) {
      const picture = banner.desktop_picture;
      if (!picture) {
        return null;
      }
      return (picture.formats && picture.formats.news_jpg) || picture.url;
    },
    formatDate(date) {
      if (!date) {
        return '—';
      }
      const [day, time] = date.split(' ');
      return `${day.split('-').reverse().join('/')} ${time.substring(0, 5)}`;
    },
    toDate(date) {
      return new Date(date.replace(' ', 'T'));
    },
    isStarted(banner) {
      return !banner.starts_at || this.toDate(banner.starts_at) <= new Date();
    },
    isEnded(banner) {
      return !!banner.ends_at && this.toDate(banner.ends_at) <= new Date();
    },
    status(banner) {
      if (banner.id === this.currentBannerId) {
        return { key: 'online', color: 'success' };
      }
      if (this.isEnded(banner)) {
        return { key: 'ended', color: 'grey' };
      }
      if (!this.isStarted(banner)) {
        return { key: 'scheduled', color: 'info' };
      }
      return { key: 'overridden', color: 'warning' };
    },
    initiateBannerDelete(banner) {
      this.bannerToDelete = banner.id;
      this.showDeletionDialog = true;
    },
    closeDeletionDialog() {
      this.bannerToDelete = null;
      this.showDeletionDialog = false;
    },
    deleteBanner() {
      this.$store.dispatch('banner/deleteBanner', this.bannerToDelete)
        .then(() => {
          this.$toasted.success(this.$t('item_deleted'));
          this.refreshData();
        })
        .catch(error => this.displayErrors(error))
        .finally(() => this.closeDeletionDialog());
    },
  },
}
</script>

<style lang="scss">
.banner-thumbnail {
  display: block;
  max-width: 240px;
  max-height: 60px;
  margin: 6px 0;
}
</style>
